<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div class="sm:flex sm:items-center sm:justify-between">
      <div>
        <div class="flex items-center space-x-3">
          <Link
            :href="route('site-owner.subscriptions.plans.show', plan.id)"
            class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <ArrowLeftIcon class="h-5 w-5" />
          </Link>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
            {{ plan.name }} - Features
          </h1>
        </div>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Configure feature limits and capabilities for this subscription plan
        </p>
      </div>
    </div>

    <!-- Plan Limits -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Plan Limits
          </h3>
          <span class="text-sm text-gray-500 dark:text-gray-400">
            {{ features.limits.length }} limits
          </span>
        </div>

        <div v-if="features.limits.length > 0" class="space-y-3">
          <div
            v-for="feature in features.limits"
            :key="feature.id"
            class="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <div class="flex-1">
              <div class="flex items-center space-x-3">
                <div>
                  <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ feature.feature_name }}
                  </h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ feature.feature_key }}
                  </p>
                  <p v-if="feature.description" class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {{ feature.description }}
                  </p>
                </div>
              </div>
            </div>
            <div class="flex items-center space-x-4">
              <div class="text-right">
                <div class="text-sm font-semibold text-gray-900 dark:text-white">
                  {{ formatFeatureValue(feature) }}
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {{ feature.value_type }}
                </div>
              </div>
              <button
                @click="editFeature(feature)"
                class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                title="Edit Value"
              >
                <PencilIcon class="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
          No limits configured for this plan.
        </p>
      </div>
    </div>

    <!-- Plan Capabilities (Active Features) -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Plan Capabilities
          </h3>
          <span class="text-sm text-gray-500 dark:text-gray-400">
            {{ activeCapabilities.length }} features
          </span>
        </div>

        <div v-if="activeCapabilities.length > 0" class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div
            v-for="feature in activeCapabilities"
            :key="feature.id"
            class="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <div class="flex-1">
              <div class="flex items-center space-x-3">
                <CheckCircleIcon
                  v-if="isFeatureEnabled(feature)"
                  class="h-5 w-5 text-green-500 dark:text-green-400 flex-shrink-0"
                />
                <XCircleIcon v-else class="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div>
                  <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ feature.feature_name }}
                  </h4>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{ feature.feature_key }}
                  </p>
                  <p v-if="feature.description" class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {{ feature.description }}
                  </p>
                </div>
              </div>
            </div>
            <button
              @click="editFeature(feature)"
              class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 ml-2"
              title="Edit Value"
            >
              <PencilIcon class="h-5 w-5" />
            </button>
          </div>
        </div>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
          No capabilities configured for this plan.
        </p>
      </div>
    </div>

    <!-- Unused Feature Set - Future Integration -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Unused Feature Set - Future Integration
          </h3>
          <span class="text-sm text-gray-500 dark:text-gray-400">
            {{ futureCapabilities.length }} features
          </span>
        </div>

        <div v-if="futureCapabilities.length > 0" class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div
            v-for="feature in futureCapabilities"
            :key="feature.id"
            class="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <div class="flex-1">
              <div class="flex items-center space-x-3">
                <CheckCircleIcon
                  v-if="isFeatureEnabled(feature)"
                  class="h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0"
                />
                <XCircleIcon v-else class="h-5 w-5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                <div>
                  <h4 class="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {{ feature.feature_name }}
                  </h4>
                  <p class="text-xs text-gray-400 dark:text-gray-500">
                    {{ feature.feature_key }}
                  </p>
                  <p v-if="feature.description" class="text-xs text-gray-400 dark:text-gray-600 mt-1">
                    {{ feature.description }}
                  </p>
                </div>
              </div>
            </div>
            <button
              @click="editFeature(feature)"
              class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 ml-2"
              title="Edit Value"
            >
              <PencilIcon class="h-5 w-5" />
            </button>
          </div>
        </div>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
          No future features configured.
        </p>
      </div>
    </div>

    <!-- Edit Feature Value Modal -->
    <div v-if="editingFeature" class="fixed z-10 inset-0 overflow-y-auto">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal"></div>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full max-w-md">
          <form @submit.prevent="submitFeature">
            <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                Edit Feature: {{ featureForm.feature_name }}
              </h3>

              <div class="space-y-4">
                <!-- Feature Info (Read-only) -->
                <div class="bg-gray-50 dark:bg-gray-900 p-3 rounded-md">
                  <div class="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    <p><span class="font-semibold">Key:</span> {{ featureForm.feature_key }}</p>
                    <p><span class="font-semibold">Type:</span> {{ featureForm.value_type }}</p>
                    <p v-if="featureForm.description"><span class="font-semibold">Description:</span> {{ featureForm.description }}</p>
                  </div>
                </div>

                <!-- Feature Value Editor -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Feature Value <span class="text-red-500">*</span>
                  </label>

                  <!-- Boolean Type -->
                  <div v-if="featureForm.value_type === 'boolean'" class="space-y-2">
                    <label class="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        v-model="featureForm.feature_value"
                        value="true"
                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span class="text-sm text-gray-900 dark:text-gray-100">Enabled</span>
                    </label>
                    <label class="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        v-model="featureForm.feature_value"
                        value="false"
                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span class="text-sm text-gray-900 dark:text-gray-100">Disabled</span>
                    </label>
                  </div>

                  <!-- Integer Type -->
                  <input
                    v-else-if="featureForm.value_type === 'integer'"
                    v-model.number="featureForm.feature_value"
                    type="number"
                    required
                    min="0"
                    class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="e.g., 100"
                  />

                  <!-- String Type -->
                  <textarea
                    v-else-if="featureForm.value_type === 'string'"
                    v-model="featureForm.feature_value"
                    rows="3"
                    required
                    class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter value"
                  ></textarea>

                  <!-- Unlimited Type -->
                  <div v-else-if="featureForm.value_type === 'unlimited'" class="text-sm text-gray-500 dark:text-gray-400">
                    This feature is set to unlimited and cannot be changed.
                  </div>
                </div>

                <!-- Helper text for special features -->
                <div v-if="featureForm.feature_key === 'modules_allowed'" class="text-xs text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                  <strong>Note:</strong> Enter modules as a JSON array, e.g.: ["it_helpdesk","maintenance","incidents"]
                </div>
              </div>
            </div>

            <div class="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                :disabled="featureFormProcessing"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {{ featureFormProcessing ? 'Saving...' : 'Update Value' }}
              </button>
              <button
                type="button"
                @click="closeModal"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import {
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/vue/24/outline';
import route from '@/Utils/route';

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  features: {
    type: Object,
    required: true,
  },
});

// Active capabilities that are currently in use
const activeCapabilityKeys = [
  'modules_allowed',
  'equipment_module',
  'assets_module',
  'document_upload',
  'sla_management',
];

// Split capabilities into active and future
const activeCapabilities = computed(() => {
  return props.features.capabilities.filter(feature =>
    activeCapabilityKeys.includes(feature.feature_key)
  );
});

const futureCapabilities = computed(() => {
  return props.features.capabilities.filter(feature =>
    !activeCapabilityKeys.includes(feature.feature_key)
  );
});

const editingFeature = ref(null);
const featureFormProcessing = ref(false);

const featureForm = reactive({
  feature_key: '',
  feature_name: '',
  feature_value: '',
  value_type: 'boolean',
  description: '',
});

const formatFeatureValue = (feature) => {
  if (feature.value_type === 'unlimited') {
    return 'Unlimited';
  }
  if (feature.value_type === 'boolean') {
    return feature.feature_value === 'true' || feature.feature_value === true ? 'Yes' : 'No';
  }
  return feature.feature_value;
};

const isFeatureEnabled = (feature) => {
  if (feature.value_type === 'boolean') {
    return feature.feature_value === 'true' || feature.feature_value === true;
  }
  return true;
};

const editFeature = (feature) => {
  editingFeature.value = feature;
  featureForm.feature_key = feature.feature_key || '';
  featureForm.feature_name = feature.feature_name || '';
  featureForm.feature_value = feature.feature_value ? String(feature.feature_value) : '';
  featureForm.value_type = feature.value_type || 'boolean';
  featureForm.description = feature.description || '';
};

const closeModal = () => {
  editingFeature.value = null;
  featureForm.feature_key = '';
  featureForm.feature_name = '';
  featureForm.feature_value = '';
  featureForm.value_type = 'boolean';
  featureForm.description = '';
};

const submitFeature = () => {
  featureFormProcessing.value = true;

  const data = {
    feature_value: featureForm.value_type === 'unlimited' ? 'unlimited' : featureForm.feature_value,
  };

  router.put(
    route('site-owner.subscriptions.plans.features.update', props.plan.id, editingFeature.value.id),
    data,
    {
      preserveScroll: true,
      onSuccess: () => {
        closeModal();
        featureFormProcessing.value = false;
      },
      onError: () => {
        featureFormProcessing.value = false;
      },
    }
  );
};
</script>
